import React, { useCallback, useEffect, createContext, ReactNode, useState } from 'react'
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail } from 'firebase/auth'
import { useSetAtom } from 'jotai'
import { userAtom } from '../stores/userAtom'
import { auth, getDb } from '../services/firebase.service'
import { doc, getDoc } from 'firebase/firestore'
import { UserDetails } from '../types/userDetails.types'
import { FirebaseCollections } from '../constants/firebase.constants'

interface AuthContextType {
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  loading: boolean
  resetPassword: (email: string) => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const setAtomUser = useSetAtom(userAtom)
  const [loading, setLoading] = useState(true)

  const loadUserDetails = useCallback(
    async (firebaseUser: NonNullable<typeof auth.currentUser>) => {
      await firebaseUser.getIdToken(true)

      const db = await getDb()
      const userDoc = await getDoc(doc(db, FirebaseCollections.USERS, firebaseUser.uid))
      if (userDoc.exists()) {
        setAtomUser({ ...firebaseUser, ...userDoc.data() } as UserDetails)
      } else {
        setAtomUser(firebaseUser)
      }
    },
    [setAtomUser]
  )

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        await loadUserDetails(firebaseUser)
      } else {
        setAtomUser(null)
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [loadUserDetails, setAtomUser])

  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password)
    await loadUserDetails(userCredential.user)
  }

  const logout = async () => {
    await signOut(auth)
    setAtomUser(null)
  }

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email.trim())
  }

  return <AuthContext.Provider value={{ login, logout, loading, resetPassword }}>{children}</AuthContext.Provider>
}
